import { useEffect, useRef, useState } from 'preact/hooks';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { useJourneyPlanning } from '../../../../data/useJourneyPlanning';
import { JourneyPlanDetailsPage } from '../DetailsPage/JourneyPlanDetailsPage';
import { JourneyPlanResultsPage } from '../ResultsPage/JourneyPlanResultsPage';
import { JourneyPlanSearchPage } from '../SearchPage/JourneyPlanSearchPage';
import { createJourneyPlanFormModel } from '../Form/PlanForm/JourneyPlanForm.types';
import {
  getJourneyPlannerDetailsPage,
  type PlanLineTimetableDetailsPageOptions,
  type PlanStopDeparturesDetailsPageOptions,
  type JourneyWidgetPage,
} from '../../../widget/pageState';
import { formatJourneySearchTimingLabel } from '../../../../data/journeyPlanSearch';
import type { JourneyPlan } from '../../../../arcticapi/types';
import { useWidgetAnalytics } from '../../../../analytics/useWidgetAnalytics';
// Styles
import styles from '../../../widget/Widget.module.css';

interface JourneyPlannerTabProps {
  currentPage: JourneyWidgetPage;
  initialOrigin?: string;
  initialDestination?: string;
  onShowResults: () => void;
  onShowItineraryDetails: (itineraryId: number) => void;
  onShowStopDeparturesDetails: (
    options: PlanStopDeparturesDetailsPageOptions,
  ) => void;
  onShowLineTimetableDetails: (
    options: PlanLineTimetableDetailsPageOptions,
  ) => void;
}

function extractPlansSummary(plans: JourneyPlan[]) {
  const operatorCodes = new Set<string>();
  const operatorNames = new Set<string>();
  const lineIds = new Set<string>();
  const modes = new Set<string>();

  for (const plan of plans) {
    for (const leg of plan.legs) {
      if (leg.operator) {
        operatorCodes.add(leg.operator.code);
        operatorNames.add(leg.operator.name);
      }
      if (leg.line?.name) lineIds.add(leg.line.name);
      modes.add(leg.mode);
    }
  }

  return {
    operatorCodes: [...operatorCodes],
    operatorNames: [...operatorNames],
    lineIds: [...lineIds],
    modes: [...modes],
  };
}

export function JourneyPlannerTab({
  currentPage,
  initialOrigin = '',
  initialDestination = '',
  onShowResults,
  onShowItineraryDetails,
  onShowStopDeparturesDetails,
  onShowLineTimetableDetails,
}: JourneyPlannerTabProps) {
  const { track } = useWidgetAnalytics();
  const [activeDetailsTab, setActiveDetailsTab] = useState<'itinerary' | 'map'>(
    'itinerary',
  );
  const planResultsWasLoadingRef = useRef(false);

  const journeyPlanning = useJourneyPlanning({
    initialOrigin,
    initialDestination,
  });
  const form = createJourneyPlanFormModel(journeyPlanning.form, {
    loading: journeyPlanning.results.loading,
  });

  const isSearchPage = currentPage.kind === 'search';
  const isResultsPage = currentPage.kind === 'results';
  const isDetailsPage = currentPage.kind === 'details';
  const selectedDetailsPage = getJourneyPlannerDetailsPage(currentPage);

  const selectedPlanIndex = selectedDetailsPage?.itineraryId ?? null;
  const selectedPlan =
    selectedPlanIndex !== null
      ? journeyPlanning.results.plans[selectedPlanIndex]
      : null;

  useEffect(() => {
    setActiveDetailsTab('itinerary');
  }, [selectedPlanIndex]);

  useEffect(() => {
    if (journeyPlanning.results.loading) {
      planResultsWasLoadingRef.current = true;
      return;
    }
    if (!planResultsWasLoadingRef.current) return;
    planResultsWasLoadingRef.current = false;

    const { plans, error } = journeyPlanning.results;
    track({
      name: 'plan_results_loaded',
      activity: 'plan',
      page: 'results',
      payload: {
        status: error ? 'error' : plans.length === 0 ? 'empty' : 'success',
        planCount: plans.length,
        ...extractPlansSummary(plans),
        ...(error ? { errorMessage: error.message } : {}),
      },
    });
  }, [
    journeyPlanning.results.loading,
    journeyPlanning.results.plans,
    journeyPlanning.results.error,
    track,
  ]);

  const timingLabel = formatJourneySearchTimingLabel({
    mode: journeyPlanning.form.time.mode,
    date: journeyPlanning.form.time.date,
    time: journeyPlanning.form.time.time,
  });

  const handleShowResults = () => {
    track({
      name: 'plan_search_submitted',
      activity: 'plan',
      page: 'search',
      payload: {
        originQuery: journeyPlanning.form.locations.originQuery,
        destinationQuery: journeyPlanning.form.locations.destinationQuery,
        originType: 'unknown',
        destinationType: 'unknown',
        timeMode: journeyPlanning.form.time.mode,
        travelModes: journeyPlanning.form.filters.value.travel,
        walkingSpeed: journeyPlanning.form.filters.value.walkingSpeed,
        minimizeWalking: journeyPlanning.form.filters.value.minimalWalking,
      },
    });
    onShowResults();
  };

  const handleDetailsTabChange = (nextTab: 'itinerary' | 'map') => {
    if (nextTab === 'map') {
      track({
        name: 'plan_details_tab_changed',
        activity: 'plan',
        page: 'details',
        payload: { tabId: nextTab, planIndex: selectedPlanIndex ?? 0 },
      });
    }
    setActiveDetailsTab(nextTab);
  };

  const onSelectPlan = (index: number) => {
    const plan = journeyPlanning.results.plans[index];
    if (plan) {
      track({
        name: 'plan_result_selected',
        activity: 'plan',
        page: 'results',
        payload: {
          planIndex: index,
          legCount: plan.legs.length,
          ...extractPlansSummary([plan]),
        },
      });
    }
    onShowItineraryDetails(index);
  };

  return (
    <div className={styles.tabWrapper}>
      <div className={styles.tabPage}>
        {isSearchPage && (
          <JourneyPlanSearchPage
            form={form}
            onShowResults={handleShowResults}
          />
        )}

        {isResultsPage && (
          <JourneyPlanResultsPage
            form={form}
            timingLabel={timingLabel}
            plans={journeyPlanning.results.plans}
            error={journeyPlanning.results.error}
            onSelectPlan={onSelectPlan}
            searchedDate={journeyPlanning.results.submittedSearch?.time.date}
            onShowEarlierJourneys={
              journeyPlanning.results.pagination.earlier
                ? () => {
                    track({
                      name: 'plan_pagination_requested',
                      activity: 'plan',
                      page: 'results',
                      payload: { direction: 'earlier' },
                    });
                    journeyPlanning.results.pagination.fetchEarlier();
                  }
                : undefined
            }
            onShowLaterJourneys={
              journeyPlanning.results.pagination.later
                ? () => {
                    track({
                      name: 'plan_pagination_requested',
                      activity: 'plan',
                      page: 'results',
                      payload: { direction: 'later' },
                    });
                    journeyPlanning.results.pagination.fetchLater();
                  }
                : undefined
            }
          />
        )}

        {isDetailsPage && (
          <JourneyPlanDetailsPage
            plan={selectedPlan}
            detailsPage={selectedDetailsPage}
            locations={
              journeyPlanning.results.submittedSearch?.locations ?? null
            }
            activeDetailsTab={activeDetailsTab}
            onTabChange={handleDetailsTabChange}
            onShowStopDeparturesDetails={onShowStopDeparturesDetails}
            onShowLineTimetableDetails={onShowLineTimetableDetails}
          />
        )}
        {journeyPlanning.form.locations.error && (
          <StateMessage
            state="error"
            title="Location lookup failed"
            message={journeyPlanning.form.locations.error.message}
          />
        )}
      </div>
    </div>
  );
}
